/**
 * The signed-in viewer's regions, in the three shapes callers need.
 *
 * Its own hook rather than a field on the mode hook, because the two answer
 * different questions and have different costs: the mode hook reads the URL,
 * so anything that only wants the viewer's regions would be dragged into
 * needing a Router it has no other use for.
 *
 * The codes are for DISPLAY and the ids are what the region and workshop APIs
 * filter on — a region may carry no code at all, so the two lists are not
 * interchangeable. `scopeRegionIds` adds every region under the assigned
 * ones, which is what decides whether the viewer may edit something.
 */
import type { CurrentStaff } from '../api/staff';
import { useCurrentUser } from '../contexts/CurrentUserContext';

export interface ViewerRegions {
  regionCodes: string[];
  regionNames: string[];
  regionIds: string[];
  scopeRegionIds: string[];
}

export function projectRegions(staff: CurrentStaff | null): ViewerRegions {
  const regions = staff?.regions ?? [];
  const regionIds = regions.map((r) => r.region.id);
  return {
    regionCodes: regions.map((r) => r.region.code).filter((c): c is string => !!c),
    regionNames: regions.map((r) => r.region.name),
    regionIds,
    // An older API that does not send the expanded list falls back to the
    // assigned regions, so a director is never locked out of their own region.
    scopeRegionIds: staff?.scopeRegionIds ?? regionIds,
  };
}

export function useViewerRegions(): ViewerRegions {
  return projectRegions(useCurrentUser().currentUser);
}
